import {
  AuthTokenDto,
  OrganizationDTOV2,
  RefreshTokenDtoV3ReturnTypeEnum,
  SignInDtoV3,
  SignInDtoV3ReturnTypeEnum,
  UserEmailDto,
} from '@workers-benefit-fund/api'

import { FUTURE_BANK } from 'src/default/config'

import { recordExplicitLogin, setAuthInfo } from '../store/auth.store'
import { isCookieAuthenticationEnabled } from '../utils/isCookieAuthenticationEnabled'

import { ApiResponseSerializer } from '@v2/commons/interface/ApiResponseSerializer'
import { IOrganizationChangeResponse } from '@v2/domains/organization/organization.types'
import { resetOrganizationSwitchSessionAfterLogin } from '@v2/domains/organization/utils/organizationSwitchSession.utils'
import {
  AuthProfileResponse,
  AuthSessionProfileResponse,
  RestorableSessionProfile,
  SessionProfile,
  toRestorableSessionProfile,
  toSessionOrganization,
  toSessionProfile,
} from '@v2/domains/user/session.mapper'
import { setUserSession } from '@v2/domains/user/store/user.store'
import { SessionOrganization } from '@v2/domains/user/user.types'
import { rootApi } from '@v2/store/api/rootApi'

export interface CookieAuthResponse {
  userId?: string
}

export type AuthResponse = AuthTokenDto | CookieAuthResponse

export interface RefreshAuthRequest {
  refreshToken?: string
  refresh_token?: string
  organizationId?: string
  membershipId?: string
}

export interface SetNewPasswordRequest {
  password: string
  bearerToken?: string
}

export const isTokenAuthResponse = (
  response: AuthResponse,
): response is AuthTokenDto => 'access_token' in response

export const prepareUserOrganizations = (
  organizations: OrganizationDTOV2[],
): SessionOrganization[] =>
  [...organizations]
    .filter((organization) => organization?.id !== FUTURE_BANK)
    .sort((firstOrganization, secondOrganization) =>
      (firstOrganization.name ?? '').localeCompare(
        secondOrganization.name ?? '',
      ),
    )
    .map(toSessionOrganization)

export const authApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation<AuthResponse, Omit<SignInDtoV3, 'returnType'>>({
      query: (queryArgs) => ({
        url: '/v3/auth/login',
        method: 'POST',
        data: {
          email: queryArgs.email.trim(),
          password: queryArgs.password,
          ...(isCookieAuthenticationEnabled && {
            returnType: SignInDtoV3ReturnTypeEnum.COOKIES,
          }),
        },
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(
            recordExplicitLogin(resetOrganizationSwitchSessionAfterLogin()),
          )
          dispatch(
            isTokenAuthResponse(data)
              ? setAuthInfo({
                  userId: data.userId,
                  access_token: data.access_token,
                  refresh_token: data.refresh_token,
                })
              : setAuthInfo({ userId: data.userId }),
          )
        } catch (error) {
          return
        }
      },
    }),
    validateMagicToken: builder.mutation<IOrganizationChangeResponse, string>({
      query: (token) => ({
        url: 'v3/auth/magic-link/login',
        method: 'POST',
        data: {
          magicLinkToken: token,
        },
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        if (isCookieAuthenticationEnabled) {
          return
        }

        try {
          const { data } = await queryFulfilled
          dispatch(
            setAuthInfo({
              userId: '',
              access_token: data.access_token,
              refresh_token: '',
            }),
          )
        } catch (error) {
          return
        }
      },
    }),
    forgotPassword: builder.mutation<void, UserEmailDto>({
      query: (queryArgs) => ({
        url: '/v3/auth/forgot-password',
        method: 'POST',
        data: {
          email: queryArgs.email.trim(),
        },
      }),
    }),
    setNewPassword: builder.mutation<{ Ok: true }, SetNewPasswordRequest>({
      query: ({ password, bearerToken }) => ({
        url: '/v3/auth/set-new-password',
        method: 'POST',
        data: {
          password,
        },
        ...(bearerToken && {
          headers: { Authorization: `Bearer ${bearerToken}` },
        }),
      }),
    }),
    logout: builder.mutation<void, void>({
      query: () => ({
        url: '/auth/logout',
        method: 'POST',
      }),
    }),
    getOrganizationsByUserId: builder.query<
      ApiResponseSerializer<SessionOrganization>,
      string
    >({
      query: (userId) => ({
        url: `/v3/auth/organizations/${userId}`,
        method: 'GET',
      }),
      transformResponse: (
        response: ApiResponseSerializer<OrganizationDTOV2>,
      ) => ({
        ...response,
        records: prepareUserOrganizations(response.records),
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled

          dispatch(
            setUserSession({
              organizations: data.records,
            }),
          )
        } catch (error) {
          return
        }
      },
    }),
    changeOrganization: builder.mutation<AuthResponse, RefreshAuthRequest>({
      query: (data) => ({
        url: '/v3/auth/refresh',
        method: 'POST',
        data: {
          organizationId: data.organizationId,
          membershipId: data.membershipId,
          ...(isCookieAuthenticationEnabled
            ? { returnType: RefreshTokenDtoV3ReturnTypeEnum.COOKIES }
            : {
                refreshToken: data.refreshToken,
                refresh_token: data.refresh_token,
              }),
        },
      }),
    }),
    getAuthProfile: builder.query<SessionProfile, null>({
      query: () => ({
        url: '/v3/auth/profile',
        method: 'GET',
      }),
      transformResponse: (response: AuthProfileResponse) =>
        toSessionProfile(response),
    }),
    getAuthSessionProfile: builder.query<RestorableSessionProfile, null>({
      query: () => ({
        url: '/v3/auth/profile',
        method: 'GET',
      }),
      transformResponse: (response: AuthSessionProfileResponse) =>
        toRestorableSessionProfile(response),
    }),
    refreshToken: builder.mutation<AuthResponse, RefreshAuthRequest>({
      query: (data) => ({
        url: '/v3/auth/refresh',
        method: 'POST',
        data: {
          ...(isCookieAuthenticationEnabled
            ? { returnType: RefreshTokenDtoV3ReturnTypeEnum.COOKIES }
            : {
                refreshToken: data.refreshToken,
                refresh_token: data.refresh_token,
              }),
          ...(data.organizationId && { organizationId: data.organizationId }),
          ...(data.membershipId && { membershipId: data.membershipId }),
        },
      }),
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(
            setAuthInfo({
              userId: data.userId,
              ...(isTokenAuthResponse(data) && {
                access_token: data.access_token,
                refresh_token: data.refresh_token,
              }),
              ...(args.organizationId && {
                organizationId: args.organizationId,
              }),
              ...(args.membershipId && {
                membershipId: args.membershipId,
              }),
            }),
          )
        } catch (error) {
          return
        }
      },
    }),
  }),
})

export const {
  useLoginMutation,
  useForgotPasswordMutation,
  useSetNewPasswordMutation,
  useLogoutMutation,
  useValidateMagicTokenMutation,
  useGetOrganizationsByUserIdQuery,
  useChangeOrganizationMutation,
  useGetAuthProfileQuery,
  useRefreshTokenMutation,
  endpoints: authApiEndpoints,
} = authApi
